import * as React from "react";
import cx from "classnames";
import { Link } from "react-router-dom";
import { JSX } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { BreadcrumbTemplateEnum, breadcrumbTemplates } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import { QESButton } from "@app/shared/qes-standards/Button";

interface BreadcrumbsProps {
  items: Array<string | Breadcrumb>;
  template?: BreadcrumbTemplateEnum;
  /**
   * Optional right-aligned content for the bar, e.g. a page-level action button. Pages that omit it
   * render exactly the markup they did before -- the extra flex/margin classes are applied only when
   * actions are present, so the shared bar's vertical rhythm is untouched everywhere else.
   */
  actions?: React.ReactNode;
}

const renderBreadcrumb = (item: string | Breadcrumb, index: number, items: Breadcrumb[]) => {
  switch (typeof item) {
    case "string":
      return (
        <li key={`${item}-${index}`} className="breadcrumb-item">
          {item}
        </li>
      );
    case "object":
      const result: JSX.Element[] = [];
      let icon: JSX.Element = null;
      if (item.iconClass) {
        if (item.iconClass.slice(0,2) == "fa") {
          icon = <i key="crumb-item" className={`fa ${item.iconClass} me-1`} />;
        } else {
          icon = <i key="crumb-item" className="me-1"><QesIcon className="breadcrumb-icon" iconName={item.iconClass} description={item.iconDescription} isDecorative={!item.iconDescription} /></i>
        }
      }
      if (item.link && index !== items.length - 1) {
        result.push(<Link key={`crumb-${index}`} to={item.link}>{icon}{item.text}</Link>);
      } else if (!!item.onClick && index !== items.length - 1) {
        result.push(
          <QESButton
            key={`crumb-${index}`}
            qesType="link"
            qesTestId={`breadcrumb__item--${index}`}
            onClick={item.onClick}
          >
            {item.text}
          </QESButton>
        );
      } else {
        result.push(<span key={`crumb-${index}`}>{icon}{item.text}</span>);
      }
      return (
        <li key={`${item.text}-${index}`} className="breadcrumb-item">
          {result}
        </li>
      );
  }
};

const Breadcrumbs: React.FunctionComponent<React.PropsWithChildren<BreadcrumbsProps>> = (props: BreadcrumbsProps) => {
  const template = breadcrumbTemplates[props.template];
  const breadcrumbItems = [...props.items];
  if (template) {
    breadcrumbItems.unshift(...template);
  }
  return (
    <>
      {
        !!props.items && (
          <nav className="page-navs">
            <div className="row w-100">
              <div className={cx("col-md-12", { "d-flex align-items-center": !!props.actions })}>
                <ol className={cx("breadcrumb", { "mb-0 mt-0": !!props.actions })}>
                  <li className="breadcrumb-item">
                    <Link to={"/"}><i className="me-1"><QesIcon className="breadcrumb-icon" iconName={QesIcons.HomeV3} isDecorative /></i>Home</Link>
                  </li>
                  {breadcrumbItems.map(renderBreadcrumb)}
                </ol>
                {!!props.actions && (
                  <div className="ms-auto d-flex align-items-center">{props.actions}</div>
                )}
              </div>
            </div>
          </nav>
        )
      }
    </>
  );
};

export default Breadcrumbs;
